import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bot, 
  Code2, 
  Globe, 
  Search, 
  Briefcase,
  Calendar,
  MessageSquare,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ServiceCard from '../components/ServiceCard';
import PersonaCard from '../components/PersonaCard';
import ContactModal from '../components/ContactModal';
import NeonBall from '../components/NeonBall';

const PERSONA_IMAGES = [
  {
    url: 'https://customer-assets.emergentagent.com/job_dmtai-ecosystem/artifacts/u2kr3av7_DMTAI%20Strategic.jpg',
    key: 'entrepreneur',
    attribution: {
      text: 'Image by freepik',
      url: 'https://www.freepik.com/free-photo/lifestyle-people-office_20256450.htm'
    }
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_dmtai-ecosystem/artifacts/7a13dhyl_DMTAI%20Physicians.jpg',
    key: 'professional',
    attribution: {
      text: 'Image by freepik',
      url: 'https://www.freepik.com/free-photo/females-doctor-hospital-with-stethoscope_12164398.htm'
    }
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_dmtai-ecosystem/artifacts/87umlqyb_DMTAI%20Owner.jpg',
    key: 'business',
    attribution: {
      text: 'Image by freepik',
      url: 'https://www.freepik.com/free-photo/medium-shot-smiley-man_16616914.htm'
    }
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_dmtai-ecosystem/artifacts/xcxyp4lm_DMTAI%20Investors.jpg',
    key: 'investor',
    attribution: {
      text: 'Image by freepik',
      url: 'https://www.freepik.com/free-photo/business-people-working-with-ipad-high-angle_37127715.htm'
    }
  }
];

export default function HomePage() {
  const { t } = useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('contact');

  const openModal = (type) => {
    setModalType(type);
    setModalOpen(true);
  };

  const services = [
    { icon: Bot, key: 'aiAgents' },
    { icon: Code2, key: 'customApps' },
    { icon: Globe, key: 'webDev' },
    { icon: Search, key: 'seo' },
    { icon: Briefcase, key: 'consulting' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 overflow-hidden" data-testid="hero-section">
        {/* Background glow */}
        <div className="hero-glow" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[var(--primary-main)] rounded-full blur-[150px] opacity-20" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-[var(--accent-gold)] rounded-full blur-[100px] opacity-10" />

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-[var(--border-highlight)]">
                <Sparkles className="w-4 h-4 text-[var(--accent-gold)]" />
                <span className="text-sm text-[var(--text-secondary)]">AI Integration Agency</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="gradient-text-gold">{t('hero.headline')}</span>
                <br />
                <span className="text-[var(--text-primary)]">{t('hero.subheadline')}</span>
              </h1>

              <p className="text-base md:text-lg text-[var(--text-secondary)] max-w-xl leading-relaxed">
                {t('hero.description')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="https://calendly.com/dmt-a-master"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-btn"
                  className="btn-primary text-center flex items-center justify-center gap-2 text-base"
                >
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5" />
                </a>
                <Link
                  to="/services"
                  data-testid="hero-services-btn"
                  className="btn-secondary text-center"
                >
                  {t('services.cta')}
                </Link>
              </div>
            </motion.div>

            {/* Right - 3D Neon Ball */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative h-[400px] md:h-[500px]"
            >
              <NeonBall />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 md:py-32 relative" data-testid="problem-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-[var(--text-primary)]">
              {t('problem.headline')}
            </h2>
            <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
              {t('problem.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 relative" data-testid="services-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--primary-main)] via-opacity-5 to-transparent opacity-30" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text-gold mb-4">
              {t('services.headline')}
            </h2>
          </motion.div>

          {/* Bento Grid for Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <div key={service.key} className={idx === 0 ? 'lg:col-span-2' : ''}>
                <ServiceCard
                  icon={service.icon}
                  title={t(`services.items.${service.key}.title`)}
                  description={t(`services.items.${service.key}.description`)}
                  index={idx}
                />
              </div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link
              to="/services"
              data-testid="services-cta-btn"
              className="btn-secondary inline-flex items-center gap-2"
            >
              {t('services.cta')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Audience Section */}
      <section className="py-20 md:py-32" data-testid="audience-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-4">
              {t('audience.headline')}
            </h2>
            <p className="text-base md:text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
              {t('audience.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {PERSONA_IMAGES.map((persona, idx) => (
              <PersonaCard
                key={persona.key}
                image={persona.url}
                title={t(`audience.personas.${persona.key}`)}
                attribution={persona.attribution}
                index={idx}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 relative" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--primary-dark)] to-transparent opacity-10" />
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text-gold">
              {t('cta.headline')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Consultation */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="glass p-6 md:p-8 rounded-2xl border border-[var(--border-subtle)] hover:border-[var(--border-highlight)] transition-all duration-300 text-center"
              data-testid="cta-consultation"
            >
              <div className="w-14 h-14 mx-auto mb-6 rounded-xl bg-[var(--primary-main)] bg-opacity-20 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-[var(--primary-light)]" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3 text-[var(--text-primary)]">
                {t('cta.consultation.title')}
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('cta.consultation.description')}
              </p>
              <a
                href="https://calendly.com/dmt-a-master"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-consultation-btn"
                className="btn-primary w-full block text-center"
              >
                {t('cta.consultation.button')}
              </a>
            </motion.div>

            {/* Demo */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="glass p-6 md:p-8 rounded-2xl border border-[var(--border-subtle)] hover:border-[var(--border-highlight)] transition-all duration-300 text-center"
              data-testid="cta-demo"
            >
              <div className="w-14 h-14 mx-auto mb-6 rounded-xl bg-[var(--accent-gold)] bg-opacity-20 flex items-center justify-center">
                <MessageSquare className="w-7 h-7 text-[var(--accent-gold)]" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3 text-[var(--text-primary)]">
                {t('cta.demo.title')}
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('cta.demo.description')}
              </p>
              <a
                href="https://wa.me/+523224750476"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-demo-btn"
                className="btn-primary w-full block text-center"
              >
                {t('cta.demo.button')}
              </a>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="glass p-6 md:p-8 rounded-2xl border border-[var(--border-subtle)] hover:border-[var(--border-highlight)] transition-all duration-300 text-center"
              data-testid="cta-services"
            >
              <div className="w-14 h-14 mx-auto mb-6 rounded-xl bg-[var(--primary-light)] bg-opacity-20 flex items-center justify-center">
                <Briefcase className="w-7 h-7 text-[var(--primary-light)]" />
              </div>
              <h3 className="text-lg md:text-xl font-bold mb-3 text-[var(--text-primary)]">
                {t('cta.services.title')}
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('cta.services.description')}
              </p>
              <Link
                to="/services"
                data-testid="cta-services-btn"
                className="btn-secondary w-full block text-center"
              >
                {t('cta.services.button')}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        type={modalType}
      />
    </div>
  );
}
